import { AppRoutes } from './routes'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useOfflineStore } from './stores/offlineStore'
import { useAuthStore } from './stores/authStore'
import { useSubscriptionStore } from './stores/subscriptionStore'
import { useCreditsStore } from './stores/creditsStore'
import OfflineIndicator from './components/OfflineIndicator'
import { Analytics } from '@vercel/analytics/react'
import { useSubscriptionCache } from './hooks/useSubscriptionCache'
import { getDocumentTitle, getBrandLogoPath, isDreambooth } from './utils/brandUtils'
import { sendAppSettingsTelemetry } from './utils/sendAppSettingsTelemetry'
import { useSessionStateStore } from './stores/sessionState'
import {
  getHostAppVersion,
  getInternetStatus,
  getSubscriptionState,
  hasElectronPreload,
  isInTauriShell,
  onInternetStatusChanged,
} from '@renderer/lib/runtime'
import { useSettingsStore } from './stores/settingsStore'

function App(): JSX.Element {
  const { i18n } = useTranslation()
  const [isOnline, setIsOnline] = useState<boolean>(true) // Assume online initially
  const { setAppVersion } = useOfflineStore()
  const suppressBrowserGestures = useSettingsStore((s) => s.suppressBrowserGestures)
  useSubscriptionCache()

  useEffect(() => {
    const isNativeShell = hasElectronPreload() || isInTauriShell()
    const root = document.documentElement
    if (!isNativeShell && suppressBrowserGestures) {
      root.classList.add('db-suppress-browser-gestures')
    } else {
      root.classList.remove('db-suppress-browser-gestures')
    }
    return (): void => {
      root.classList.remove('db-suppress-browser-gestures')
    }
  }, [suppressBrowserGestures])

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage)
    }

    document.title = getDocumentTitle()
    const iconPath = isDreambooth() ? '/favicon.ico' : getBrandLogoPath()
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
    if (link) {
      link.href = iconPath
    } else {
      const newLink = document.createElement('link')
      newLink.rel = 'icon'
      newLink.href = iconPath
      document.head.appendChild(newLink)
    }

    void getInternetStatus().then(setIsOnline)
    const unsubscribe = onInternetStatusChanged(setIsOnline)
    void getHostAppVersion().then((v) => {
      if (v) setAppVersion(v)
    })
    return (): void => {
      unsubscribe()
    }
  }, [i18n])

  // Electron-only bootstrap: init cookie, load cached subscription from SQLite, then verify in background
  useEffect(() => {
    if (!hasElectronPreload()) return

    const bootstrap = async (): Promise<void> => {
      try {
        // 1. Initialize cookie (moved from ProtectedRoute)
        const cookieOk = await useAuthStore.getState().initializeCookie()

        // 2. Cross-check persisted subscription against SQLite (has server-aware expiry data).
        //    subscriptionStore and creditsStore now use Zustand persist, so they already
        //    have the last-known values from localStorage. SQLite is the authority on
        //    whether the subscription has actually expired (endDate + serverTimeOffset).
        const cached = await getSubscriptionState()
        if (cached?.isValid) {
          const now = Math.floor(Date.now() / 1000)
          const adjustedNow = now + (cached.serverTimeOffset || 0)
          if (!cached.endDate || cached.endDate > adjustedNow) {
            useSubscriptionStore.getState().setSubscription({
              type: cached.type || 'free',
              plan: cached.plan || null,
              isValid: true,
              deviceId: cached.deviceId || undefined,
              activationCode: cached.activationCode || undefined
            })
            useCreditsStore.setState({
              credits: 'Unlimited',
              package: cached.type || 'free',
              error: null
            })
          } else {
            // SQLite says subscription expired -- clear stale persisted state
            useSubscriptionStore.getState().clearSubscription()
            useCreditsStore.setState({ credits: 0, package: 'free' })
          }
        } else if (useSubscriptionStore.getState().isValid) {
          // localStorage says valid but SQLite disagrees (cleared/missing) -- reconcile
          // Don't clear yet; the background fetch (step 3) will re-validate.
          // This handles the case where SQLite was wiped but localStorage wasn't.
        }

        // 3. Start background subscription + credits validation
        //    Only run when online AND cookie init succeeded so we do not spam the API with
        //    unauthenticated requests before the session cookie exists.
        if (cookieOk && useAuthStore.getState().isAuthenticated) {
          const isCurrentlyOnline = await getInternetStatus()
          if (isCurrentlyOnline) {
            useCreditsStore.getState().fetchSubscriptionAndCredits()
          }
        } else if (!cookieOk) {
          console.log('[APP] Cookie init failed — skipping subscription fetch, user will be redirected to login')
        }
      } catch (error) {
        console.error('[APP] Bootstrap error:', error)
      }
    }

    bootstrap()
  }, [])

  // Periodic heartbeat: send telemetry once per hour regardless of current page.
  // Server-side cron purges entries older than DEVICE_MONITORING_CONFIG.RETENTION_DAYS.
  useEffect(() => {
    if (typeof window.electron === 'undefined') return
    const HEARTBEAT_INTERVAL_MS = 60 * 60 * 1000
    const interval = setInterval(() => {
      const projectId = useSessionStateStore.getState().projectId || undefined
      sendAppSettingsTelemetry({ projectId }).catch((err) => {
        console.warn('[HEARTBEAT] Failed:', err)
      })
    }, HEARTBEAT_INTERVAL_MS)
    return (): void => { clearInterval(interval) }
  }, [])

  return (
    <div>
      <OfflineIndicator isOnline={isOnline} />
      <AppRoutes />
      <Analytics />
    </div>
  )
}

export default App
